import { useEffect, useState } from 'react';
import { fetchWithCorpId } from '../utils/api';
import { useParams } from 'react-router-dom';

const LABEL = {
  fontSize: '11px', fontWeight: '700', color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.6px',
  marginBottom: '6px', display: 'block',
};

const INPUT_BASE = {
  width: '100%', padding: '10px 14px', borderRadius: '8px',
  border: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
  background: '#fff', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s, box-shadow 0.18s',
  fontFamily: 'inherit',
};

const INPUT_READONLY = {
  ...INPUT_BASE,
  background: '#f8fafc', color: '#94a3b8',
  cursor: 'not-allowed', borderColor: '#f1f5f9',
};

function AdminForm({ mode = 'create', onSuccess, onCancel }) {
  const { id } = useParams();
  const isEdit = mode === 'edit';
  const [admin, setAdmin] = useState({ feature: '', description: '', value: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    fetchWithCorpId(`/admin/${id}`)
      .then(data => setAdmin(data))
      .catch(err => setMessage('Error loading data: ' + err.message))
      .finally(() => setLoading(false));
  }, [isEdit, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdmin(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = admin.id ? 'PUT' : 'POST';
      const url = admin.id ? `/admin/${admin.id}` : '/admin';
      await fetchWithCorpId(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...admin,
          corpId: localStorage.getItem('selectedCorpId') || '0',
          updateDate: new Date().toISOString(),
        }),
      });
      onSuccess();
    } catch {
      setMessage('error:Failed to save. Please try again.');
    }
  };

  const isError = message.startsWith('error:');
  const displayMsg = isError ? message.slice(6) : message;

  return (
    <div style={{ padding: '32px', maxWidth: '760px', margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
          }} />
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase' }}>
            Admin Settings
          </span>
        </div>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>
          {isEdit ? 'Edit Configuration' : 'View Configuration'}
        </h2>
        <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#64748b' }}>
          {isEdit ? 'Update the configuration value for this feature.' : 'View system configuration details.'}
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#94a3b8', fontSize: '14px' }}>
          Loading...
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: '16px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
          border: '1px solid #f1f5f9', overflow: 'hidden',
        }}>
          {/* Card header bar */}
          <div style={{
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '9px', flexShrink: 0,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <div>
              <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>Configuration Details</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>Read-only fields are system-managed</div>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '0', maxWidth: '100%' }}>

            {/* Row 1: ID + Category */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={LABEL}>Admin ID</label>
                <input type="text" name="id" value={admin.id || ''} readOnly style={INPUT_READONLY} />
              </div>
              <div>
                <label style={LABEL}>Category</label>
                <input type="text" name="category" value={admin.category || ''} readOnly style={INPUT_READONLY} />
              </div>
            </div>

            {/* Feature */}
            <div style={{ marginBottom: '20px' }}>
              <label style={LABEL}>Feature</label>
              <input type="text" name="feature" value={admin.feature || ''} readOnly style={INPUT_READONLY} />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={LABEL}>Description</label>
              <textarea
                name="description"
                value={admin.description || ''}
                rows={3}
                readOnly
                style={{ ...INPUT_READONLY, resize: 'vertical' }}
              />
            </div>

            {/* Divider before editable field */}
            <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0 20px' }} />

            {/* Value — editable */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ ...LABEL, color: '#3b82f6' }}>
                Value
                <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>
                <span style={{
                  marginLeft: '8px', fontSize: '10px', fontWeight: '600',
                  padding: '2px 7px', borderRadius: '4px',
                  background: '#eff6ff', color: '#3b82f6', textTransform: 'none',
                  letterSpacing: '0.2px',
                }}>Editable</span>
              </label>
              <input
                type="text"
                name="value"
                value={admin.value || ''}
                onChange={handleChange}
                required
                style={INPUT_BASE}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
                This is the only configurable field for this setting.
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 28px', borderRadius: '9px',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  border: 'none', color: 'white', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
                  transition: 'all 0.18s ease',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(59,130,246,0.45)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.35)';
                }}
              >
                Save Changes
              </button>

              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  style={{
                    padding: '10px 24px', borderRadius: '9px',
                    background: 'transparent', border: '1px solid #e2e8f0',
                    color: '#64748b', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600',
                    transition: 'all 0.18s ease',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.color = '#475569';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Message */}
            {displayMsg && (
              <div style={{
                marginTop: '16px', padding: '12px 16px', borderRadius: '9px',
                background: isError ? '#fef2f2' : '#f0fdf4',
                border: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
                color: isError ? '#dc2626' : '#16a34a',
                fontSize: '13px', fontWeight: '500',
              }}>
                {displayMsg}
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

export default AdminForm;
